import PhoneBoothCloseup from 'assets/hero/phonebooth_closeUp.webp';
import PhoneBoothCloseupMobile from 'assets/hero/phonebooth_closeUp_mobile.webp';
import phoneBoothLightOff from 'assets/hero/phonebooth_lightOff.webp';
import phoneBoothLightOn from 'assets/hero/phonebooth_lightOn.webp';
import PhoneBoothMobile from 'assets/hero/phonebooth_mobile.webp';
import PhoneBoothScreenLightOff from 'assets/hero/phonebooth_screen_background_lightOff.webp';
import PhoneBoothScreenLightOn from 'assets/hero/phonebooth_screen_background_lightOn.webp';
import { PhoneCall } from 'phosphor-react';
import styled, { css, keyframes } from 'styled-components/macro';

const FooterHeight = 95;

export const MainWrapper = styled.div<{
  experienceStarted: boolean;
}>`
  position: relative;
  width: 100%;
  height: calc(100vh + ${FooterHeight}px);

  ${({ theme, experienceStarted }) => theme.mediaWidth.upToMedium`
    height: 100vh;
    height: var(--doc-height);
    overflow: ${!experienceStarted && 'hidden'};
  `};
`;

const fadeOut = keyframes`
  0% {
    opacity: 1;
  }
  100% {
    opacity: 0;
  }
`;

const fadein = keyframes`
  0% {
    opacity: 0; 
  }
  100% { 
    opacity: 1; 
  }
`;

export const HeroWrapper = styled.section<{
  sizeRatio: number;
  experienceStarted: boolean;
  isShowingPhoneScreen: boolean;
}>`
  position: relative;
  width: auto;
  height: ${({ sizeRatio, isShowingPhoneScreen }) => (isShowingPhoneScreen ? `${1080 * sizeRatio * 0.9}px` : '100vh')};
  max-height: 100vh;

  ${({ theme, experienceStarted }) => theme.mediaWidth.upToMedium`
    height: 100vh;
    height: var(--doc-height);
    overflow: ${!experienceStarted && 'hidden'};
  `};
  ${({ theme }) => theme.mediaWidth.upToExtraSmall`
    overflow-x: clip;
  `};
`;

export const FooterWrapper = styled.footer<{ experienceStarted: boolean }>`
  z-index: 4;
  position: relative;
  bottom: 0;
  width: 100%;

  ${({ theme, experienceStarted }) => theme.mediaWidth.upToMedium`
    display: ${experienceStarted ? 'block' : 'none'};
    opacity: 0;
    animation: ${
      experienceStarted &&
      css`
        ${fadein} 2s linear forwards
      `
    };
  `}
`;

const phoneBoothLightAnim = keyframes`
  0% {
    opacity: 1;
  }
  3% {
    opacity: 0.05;
  }
  6% {
    opacity: 1;
  }
  9% {
    opacity: 0;
  }
  12% {
    opacity: 1;
  }
  40% {
    opacity: 1;
  }
  50% {
    opacity: 0.6;
  }
  55% {
    opacity: 0.9;
  }
  57% {
    opacity: 0.6;
  }
  59% {
    opacity: 1;
  }
  100% {
    opacity: 1;
  }
`;

export const HeroBottomGradient = styled.div`
  z-index: 4;
  position: absolute;
  bottom: 0;
  width: 100%;
  height: 50px;
  object-fit: contain;
  background-image: linear-gradient(to top, #000, rgba(25, 56, 50, 0) 94%);
`;

export const StarterBackgroundWrapper = styled.div<{ isFadingOut: boolean }>`
  position: relative;
  width: 100%;
  height: 100%;
  animation: ${({ isFadingOut }) =>
    isFadingOut
      ? css`
          ${fadeOut} 1s ease-in-out
        `
      : 'none'};
`;

export const LightOnBackground = styled.div<{ experienceStarted: boolean }>`
  z-index: 1;
  position: absolute;
  width: 100%;
  height: 100%;

  background: url(${phoneBoothLightOn});
  background-repeat: no-repeat;
  background-position: center;
  background-size: cover;

  animation-delay: 0s;
  animation: ${phoneBoothLightAnim} 5s infinite;

  ${({ theme, experienceStarted }) => theme.mediaWidth.upToMedium`
    display: ${experienceStarted ? 'block' : 'none'};
  `}

  ${({ theme }) => theme.mediaWidth.upToExtraSmall`
    transform: translateX(10px) translateY(-100px) scale(1.3);
    background-size: auto 100%;
  `}
`;
export const LightOffBackground = styled.div`
  z-index: 0;
  position: absolute;
  width: 100%;
  height: 100%;

  background: url(${phoneBoothLightOff});
  background-repeat: no-repeat;
  background-position: center;
  background-size: cover;

  ${({ theme }) => theme.mediaWidth.upToExtraSmall`
    transform: translateX(10px) translateY(-100px) scale(1.3);
    background-size: auto 100%;
  `};
`;

const InteractionAnim = keyframes`
    0%{
      transform: translateX(-50%) rotate(0deg) scale(1);
    }
    10%{
      transform: translateX(-50%) rotate(-10deg) scale(1.05);
    }
    20%{
      transform: translateX(-50%) rotate(10deg) scale(1.05);
    }
    30%{
      transform: translateX(-50%) rotate(-10deg) scale(1.05);
    }
    40%{
      transform: translateX(-50%) rotate(10deg) scale(1.05);
    }
    50%{
      transform: translateX(-50%) rotate(0deg) scale(1);
    }
    100%{
      transform: translateX(-50%) rotate(0deg) scale(1);
    }
`;

export const PhoneCallIcon = styled(PhoneCall)`
  position: absolute;
  top: 40%;
  left: 50%;
  transform: translateX(-50%);
  color: white;
  width: 40px;
  height: 40px;
  animation: ${InteractionAnim} 0.8s ease-in-out;
  animation-iteration-count: infinite;
  background-color: #0000006c;
  padding: 6px 7px 4px 5px;
  border-radius: 100%;
`;

export const PhoneBoothClicker = styled.div<{ experienceStarted: boolean }>`
  z-index: 5;
  cursor: pointer;
  position: absolute;
  top: 45.8%;
  left: 36%;
  height: 32%;
  width: 25%;
  transform: translateX(-50%);
  border-radius: 30px;

  &:hover ${PhoneCallIcon} {
    animation: none;
    transform: translateX(-50%) scale(1.1);
  }

  ${({ theme, experienceStarted }) => theme.mediaWidth.upToMedium`
    display: ${experienceStarted ? 'block' : 'none'};
  `}
  ${({ theme }) => theme.mediaWidth.upToExtraSmall`
    transform: translateX(-65%) translateY(-85px) scale(1.3);
  `}

  @media (min-aspect-ratio: 6/9) {
    top: 45.8%;
    left: 38%;
    height: 32%;
    width: 19%;
  }
  @media (min-aspect-ratio: 7/9) {
    top: 45.8%;
    left: 40%;
    height: 32%;
    width: 16%;
  }
  @media (min-aspect-ratio: 8/9) {
    top: 45.8%;
    left: 41%;
    height: 32%;
    width: 14.5%;
  }
  @media (min-aspect-ratio: 9/9) {
    top: 45.8%;
    left: 42.5%;
    height: 32%;
    width: 13%;
  }
  @media (min-aspect-ratio: 11/9) {
    top: 45.8%;
    left: 43.5%;
    height: 32%;
    width: 11%;
  }
  @media (min-aspect-ratio: 12/9) {
    top: 45.8%;
    left: 44.2%;
    height: 32%;
    width: 9.2%;
  }
  @media (min-aspect-ratio: 15/9) {
    top: 45.8%;
    left: 45.5%;
    height: 32%;
    width: 8%;
  }
  @media (min-aspect-ratio: 16/9) {
    top: 45.8%;
    left: 45.5%;
    height: 34%;
    width: 8%;
  }
  @media (min-aspect-ratio: 19/9) {
    top: 45.8%;
    left: 45.5%;
    height: 42%;
    width: 8%;
  }
  @media (min-aspect-ratio: 20/9) {
    top: 45.8%;
    left: 45.5%;
    height: 45%;
    width: 8%;
  }
`;

const fadeInOutZoom = keyframes`
  0% {
    opacity: 0;
    background-size: 110%;
  }
  20% {
    opacity: 1;
  }
  60% {
    opacity: 1;
  }
  100% {
    opacity: 0;
    background-size: 140%;
  }
`;
const fadeInOutZoomMobile = keyframes`
  0% {
    opacity: 0;
    background-size: 130%;
  }
  20% {
    opacity: 1;
  }
  60% {
    opacity: 1;
  }
  100% {
    opacity: 0;
    background-size: 140%;
  }
`;

export const CloseUpBackground = styled.div<{ isFadingOut: boolean }>`
  position: relative;
  width: 100%;
  height: 100%;
  animation: ${fadeInOutZoom} 4s ease-in-out;

  background: url(${PhoneBoothCloseup});
  background-repeat: no-repeat;
  background-position: center;
  background-size: 140%;
  opacity: 0;

  ${({ theme }) => theme.mediaWidth.upToMedium`
    background: url(${PhoneBoothCloseupMobile});
    background-size: 140%;
    background-position: center;
    animation: ${fadeInOutZoomMobile} 4s ease-in-out;
  `};
`;

export const CloseupBackground = styled.div`
  z-index: 0;
  position: absolute;
  width: 100%;
  height: 100%;
`;

const blinkindFadeIn = keyframes`
  0% {
    opacity: 0;
  }
  10% {
    opacity: 1;
  }
  20% {
    opacity: 0.8;
  }
  25% {
    opacity: 1;
  }
  31% {
    opacity: 0.4;
  }
  35% {
    opacity: 0.3;
  }
  45% {
    opacity: 1;
  }
  100% {
    opacity: 1;
  }
`;

export const PhoneScreenWrapper = styled.div<{ sizeRatio: number }>`
  height: 100%;
  width: 100%;

  animation: ${blinkindFadeIn} 5s linear;
`;

const boothAnim = keyframes`
  0% {
    opacity: 0;
  }
  50% {
    opacity: 1;
  }
  100% {
    opacity: 0;
  }
`;
export const BackgroundLightOff = styled.div<{ sizeRatio: number }>`
  z-index: 0;
  position: absolute;
  height: 100%;
  width: 100%;

  background-image: url(${PhoneBoothScreenLightOff});
  background-size: ${({ sizeRatio }) => `${1920 * sizeRatio}px ${1080 * sizeRatio}px`};
  background-position: top center;
  background-repeat: no-repeat;

  ${({ theme }) => theme.mediaWidth.upToMedium`
    display; none;
  `};
`;
export const BackgroundLightOn = styled.div<{ sizeRatio: number }>`
  z-index: 1;
  position: absolute;
  height: 100%;
  width: 100%;

  animation-delay: 0s;
  animation: ${boothAnim} 3s infinite;

  background-image: url(${PhoneBoothScreenLightOn});
  background-size: ${({ sizeRatio }) => `${1920 * sizeRatio}px ${1080 * sizeRatio}px`};
  background-position: top center;
  background-repeat: no-repeat;

  ${({ theme, sizeRatio }) => theme.mediaWidth.upToMedium`
    background-image: url(${PhoneBoothMobile});
    background-size: ${`${900 * sizeRatio}px ${1600 * sizeRatio}px;`};
  `};
  ${({ theme, sizeRatio }) => theme.mediaWidth.upToMedium`
    background-position: ${`center -${150 * sizeRatio}px`};
  `};
  ${({ theme }) => theme.mediaWidth.upToExtraSmall`
    background-position: top center;
  `};
  ${({ theme }) => theme.mediaWidth.upToMedium`
    animation: ${fadein} 1s linear;
  `};
`;

export const HeroBottomWrapper = styled.div`
  z-index: 2;
  display: flex;
  align-items: flex-end;
  justify-content: space-between;

  position: absolute;
  bottom: 30px;
  margin-bottom: 30px;
  width: 100%;

  ${({ theme }) => theme.mediaWidth.upToMedium`
    flex-direction: column;
    align-items: center;
    position: absolute;
    bottom: 10%;
    top: auto;
    margin-bottom: 0;
  `}
`;

export const BRXVerticalLogoWrapper = styled.a`
  z-index: 2;
  position: absolute;
  top: 5%;
  left: 5%;
  ${({ theme }) => theme.mediaWidth.upToMedium`
    display: none;
  `}
  display: block;
`;
export const BRXVerticalLogoImg = styled.img`
  width: 32px;
  height: auto;
`;

export const BRXHorizontalLogoWrapper = styled.a<{ experienceStarted: boolean }>`
  z-index: 2;
  position: absolute;
  top: 10%;
  left: 50%;
  transform: translateX(-50%);
  ${({ theme, experienceStarted }) => theme.mediaWidth.upToMedium`
    display: block;
    opacity: 0;
    animation: ${
      experienceStarted &&
      css`
        ${fadein} 2s linear forwards
      `
    };
  `}
  display: none;
`;
export const BRXHorizontalLogoImg = styled.img`
  height: 43px;
  width: auto;
`;

export const ConnectWalletWrapper = styled.div<{ experienceStarted: boolean }>`
  z-index: 2;
  position: absolute;
  top: 5%;
  right: 5%;
  ${({ theme, experienceStarted }) => theme.mediaWidth.upToMedium`
    display: block;
    opacity: 0;
    animation: ${
      experienceStarted &&
      css`
        ${fadein} 2s linear forwards
      `
    };
  `}
`;

export const SocialsWrapper = styled.div`
  z-index: 2;
  padding-left: 5%;

  ${({ theme }) => theme.mediaWidth.upToMedium`
    display: none;
  `}
`;

export const SoundPlayerWrapper = styled.div<{ experienceStarted: boolean }>`
  z-index: 2;
  padding-right: 50px;

  ${({ theme, experienceStarted }) => theme.mediaWidth.upToMedium`
    width: 100%;
    padding-right: 0;
    display:flex;
    justify-content: center;
    opacity: 0;
    ${
      experienceStarted &&
      css`
        animation-delay: 0s;
        animation: ${fadein} 2s linear forwards;
      `
    }
  `}
`;

export const ShowMoreAnimation = keyframes`
  0%{
    transform: translateY(0px);
  }
  50%{
    transform: translateY(2px);
  }
  100%{
    transform: translateY(0px);
  }
`;
